import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import formService, { SYSTEM_SELECT_CATEGORY } from '../services/formService'
import optionService from '../services/optionService'
import settingService, { toMap } from '../services/settingService'
import { getTracking } from '../utils/tracking'
import toast from 'react-hot-toast'
import {
  HiOutlineCheckCircle, HiOutlineArrowRight, HiOutlineArrowLeft,
  HiOutlineCloudUpload, HiOutlineBriefcase, HiOutlineDocumentText, HiOutlineShieldCheck,
} from 'react-icons/hi'

const MAX_SIZE = 5 * 1024 * 1024

// ─── 1 trường động ─────────────────────────────────────────────────
function DynamicField({ field, value, onChange, options, uploading, onFile }) {
  const { field_type: t, field_key: key, label, is_required, placeholder } = field
  const req = is_required && <span className="text-accent-500"> *</span>

  // chọn option cho select: system -> FormOption theo category; custom -> field.options
  const selectOptions = SYSTEM_SELECT_CATEGORY[key]
    ? (options[SYSTEM_SELECT_CATEGORY[key]] || []).map((o) => o.value)
    : (field.options || [])

  const base = 'input'
  const lbl = <label className="label">{label}{req}</label>

  if (t === 'textarea') {
    return <div className="md:col-span-2">{lbl}
      <textarea className="input min-h-[90px]" value={value || ''} placeholder={placeholder || ''}
        onChange={(e) => onChange(e.target.value)} /></div>
  }
  if (t === 'select') {
    return <div>{lbl}
      <select className="select" value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">-- Chọn --</option>
        {selectOptions.map((o) => <option key={o} value={o}>{o}</option>)}
      </select></div>
  }
  if (t === 'multiselect') {
    const arr = Array.isArray(value) ? value : []
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])
    return <div className="md:col-span-2">{lbl}
      <div className="flex flex-wrap gap-2">
        {selectOptions.map((o) => (
          <button type="button" key={o} onClick={() => toggle(o)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${arr.includes(o)
              ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-secondary-600 border-secondary-200 hover:border-brand-400'}`}>
            {o}
          </button>
        ))}
      </div></div>
  }
  if (t === 'boolean') {
    return <div>{lbl}
      <div className="flex gap-2">
        {[['true', 'Có'], ['false', 'Không']].map(([v, t2]) => (
          <button type="button" key={v} onClick={() => onChange(v)}
            className={`flex-1 px-3 py-2.5 rounded-lg text-sm border ${String(value) === v
              ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-secondary-600 border-secondary-200 hover:border-brand-400'}`}>
            {t2}
          </button>
        ))}
      </div></div>
  }
  if (t === 'file' || t === 'image') {
    const accept = t === 'image' ? 'image/*' : '.pdf,.doc,.docx'
    return <div>{lbl}
      <label className="border-2 border-dashed border-secondary-200 rounded-lg px-3 py-2.5 text-center cursor-pointer hover:border-brand-400 hover:bg-brand-50/40 transition-colors block">
        <input type="file" accept={accept} className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])} />
        {uploading ? (
          <span className="inline-flex items-center gap-2 text-sm text-secondary-500 py-1"><span className="spinner w-4 h-4" /> Đang tải lên…</span>
        ) : value?.filename ? (
          <span className="inline-flex items-center gap-2 text-sm text-emerald-600 py-1">
            <HiOutlineCheckCircle className="w-5 h-5" /> <span className="truncate max-w-[180px]">{value.filename}</span>
          </span>
        ) : (
          <span className="inline-flex flex-col items-center text-secondary-400 py-1">
            <HiOutlineCloudUpload className="w-6 h-6 text-brand-400" />
            <span className="text-xs mt-0.5">{t === 'image' ? 'Tải ảnh lên' : 'Kéo thả / chọn file (PDF, DOC, DOCX)'}</span>
            <span className="text-[10px]">Tối đa 5MB</span>
          </span>
        )}
      </label></div>
  }
  // text | email | phone | number | date | url
  const inputType = { number: 'number', date: 'date', email: 'email', url: 'url' }[t] || 'text'
  const onlyDigits = t === 'phone' || t === 'number'
  return <div>{lbl}
    <input type={onlyDigits ? 'text' : inputType} className={base} value={value || ''} placeholder={placeholder || ''}
      inputMode={t === 'phone' ? 'tel' : onlyDigits ? 'numeric' : undefined}
      maxLength={t === 'phone' ? 11 : undefined}
      onChange={(e) => onChange(onlyDigits ? e.target.value.replace(/[^\d]/g, '') : e.target.value)} /></div>
}

export default function CampaignForm() {
  const { slug } = useParams()
  const [form, setForm] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [options, setOptions] = useState({})
  const [logo, setLogo] = useState('/logo-dxmt.png')
  const [values, setValues] = useState({})
  const [uploadingKey, setUploadingKey] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    formService.getBySlug(slug)
      .then((res) => {
        if (res.status === 'success') {
          setForm(res.data)
          document.title = `${res.data.title} | Tuyển dụng Đất Xanh Miền Trung`
        }
      })
      .catch(() => setNotFound(true))
    optionService.getPublic().then((r) => { if (r.status === 'success') setOptions(r.data) }).catch(() => {})
    settingService.list().then((r) => {
      if (r.status === 'success') { const m = toMap(r.data); if (m.brand_logo) setLogo(m.brand_logo) }
    }).catch(() => {})
  }, [slug])

  const setVal = (k, v) => setValues((p) => ({ ...p, [k]: v }))

  const handleFile = async (field, file) => {
    if (!file) return
    if (file.size > MAX_SIZE) { toast.error('File vượt quá 5MB'); return }
    setUploadingKey(field.field_key)
    try {
      const res = await formService.upload(file, field.field_type === 'image' ? 'image' : 'cv')
      if (res.status === 'success') { setVal(field.field_key, { url: res.data.url, filename: res.data.filename }); toast.success('Đã tải lên') }
    } catch { /* interceptor */ } finally { setUploadingKey(null) }
  }

  const submit = async (e) => {
    e.preventDefault()
    // validate required
    for (const f of form.fields) {
      const v = values[f.field_key]
      const empty = (f.field_type === 'file' || f.field_type === 'image') ? !v?.url
        : Array.isArray(v) ? v.length === 0 : !(v ?? '').toString().trim()
      if (f.is_required && empty) { toast.error(`Vui lòng nhập "${f.label}"`); return }
      const sv = (v ?? '').toString().trim()
      if (sv && f.field_type === 'phone' && !/^0\d{9,10}$/.test(sv)) { toast.error(`"${f.label}": SĐT không hợp lệ (10–11 số, bắt đầu 0)`); return }
      if (sv && f.field_type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sv)) { toast.error(`"${f.label}": email không hợp lệ`); return }
    }
    setSubmitting(true)
    try {
      const res = await formService.submit(slug, values, getTracking())
      if (res.status === 'success') { setDone(true); window.scrollTo({ top: 0 }) }
    } catch { /* interceptor */ } finally { setSubmitting(false) }
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-50 px-4 text-center">
        <div>
          <h1 className="text-2xl font-bold text-brand-700 mb-2">Không tìm thấy form tuyển dụng</h1>
          <p className="text-secondary-500 mb-6">Liên kết có thể đã bị thay đổi hoặc ngừng tuyển.</p>
          <Link to="/" className="btn-primary">Về trang chủ</Link>
        </div>
      </div>
    )
  }
  if (!form) {
    return <div className="min-h-screen flex items-center justify-center bg-brand-50"><div className="spinner w-8 h-8" /></div>
  }

  return (
    <div className="min-h-screen bg-brand-50/40">
      <header className="bg-white border-b border-secondary-100">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/"><img src={logo} alt="Logo" className="h-9 w-auto" /></Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-secondary-500 hover:text-brand-500">
            <HiOutlineArrowLeft className="w-4 h-4" /> Trang chủ
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {done ? (
          <div className="card p-8 text-center">
            <HiOutlineCheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-secondary-900 mb-2">Gửi hồ sơ thành công!</h2>
            <p className="text-secondary-500 mb-6">{form.thank_you || 'Cảm ơn bạn đã ứng tuyển. Chúng tôi sẽ liên hệ sớm.'}</p>
            <Link to="/" className="btn-outline">Về trang chủ</Link>
          </div>
        ) : (
          <>
            {/* Hero chiến dịch */}
            <div className="mb-6">
              {form.position && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-sm font-medium mb-3">
                  <HiOutlineBriefcase className="w-4 h-4" /> {form.position}
                </span>
              )}
              <h1 className="text-3xl sm:text-4xl font-extrabold text-brand-700">{form.title}</h1>
              {form.description && <p className="mt-3 text-secondary-600">{form.description}</p>}
            </div>

            {form.hero_image && (
              <div className="rounded-2xl overflow-hidden shadow-soft mb-6">
                <img src={form.hero_image} alt="" className="w-full object-cover max-h-72" />
              </div>
            )}
            {form.intro_content && (
              <div className="card p-5 mb-6 text-secondary-600 whitespace-pre-line">{form.intro_content}</div>
            )}

            <form onSubmit={submit} className="bg-white rounded-2xl shadow-soft border border-secondary-100 p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-5">
                <HiOutlineDocumentText className="w-5 h-5 text-brand-500" />
                <h2 className="font-bold text-brand-700">Thông tin ứng tuyển</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {form.fields.map((f) => (
                  <DynamicField key={f.id || f.field_key} field={f}
                    value={values[f.field_key]} onChange={(v) => setVal(f.field_key, v)}
                    options={options} uploading={uploadingKey === f.field_key}
                    onFile={(file) => handleFile(f, file)} />
                ))}
              </div>

              <p className="flex items-center gap-1.5 text-xs text-secondary-400 mt-5">
                <HiOutlineShieldCheck className="w-4 h-4" /> Thông tin của bạn được bảo mật, chỉ dùng cho mục đích tuyển dụng.
              </p>
              <div className="flex justify-center mt-6">
                <button type="submit" disabled={submitting || uploadingKey}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-accent-500 text-white font-semibold shadow-lg shadow-accent-500/30 hover:bg-accent-600 transition-colors disabled:opacity-60">
                  {submitting ? 'Đang gửi…' : <>Gửi hồ sơ ứng tuyển <HiOutlineArrowRight className="w-5 h-5" /></>}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
